import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Zap, Menu, X } from 'lucide-react';
import TestimonialBanner from '@/components/TestimonialBanner';
import { apiFetch, getStoredToken } from '@/lib/api';
import { GuestChatConsole } from '@/components/landing/GuestChatConsole';
import { GetStartedModal } from '@/components/landing/GetStartedModal';
import { ServiceCard } from '@/components/landing/ServiceCard';
import { WebPackageCard } from '@/components/landing/WebPackageCard';
import { ManagedPlanCard } from '@/components/landing/ManagedPlanCard';
import { SectionHeading, DiagnosticBanner, HowItWorks, MissionStrip, LandingFooter } from '@/components/landing/LandingSections';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NAV = [['services', 'Services'], ['web-builds', 'Web Builds'], ['managed', 'Managed'], ['how-it-works', 'How it works']];

function scrollToId(id) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function TopNav({ user, onChat }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const accountBtn = user
    ? { label: 'My tickets', to: user.role === 'admin' ? '/admin' : '/dashboard', id: 'nav-link-my-tickets' }
    : { label: 'Sign in', to: '/login', id: 'nav-link-sign-in' };
  return (
    <nav className="sticky top-0 z-40 backdrop-blur-md bg-[#0a0a1f]/85 border-b border-[#00f0ff]/20">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="flex items-center gap-2" data-testid="nav-brand-logo">
          <Zap size={20} className="text-[#00f0ff]" />
          <span className="font-heading font-extrabold uppercase tracking-tight text-white">Next Wave <span className="text-[#ff00ff]">Tech Help</span></span>
        </button>
        <div className="hidden md:flex items-center gap-7">
          {NAV.map(([id, label]) => (
            <button key={id} onClick={() => scrollToId(id)} className="font-tech text-xs uppercase tracking-widest text-slate-300 hover:text-[#00f0ff] transition-colors" data-testid={`nav-link-${id}`}>
              {label}
            </button>
          ))}
          <button onClick={() => navigate(accountBtn.to)} className="font-tech text-xs uppercase tracking-widest text-slate-400 hover:text-white transition-colors" data-testid={accountBtn.id}>
            {accountBtn.label}
          </button>
          <button onClick={onChat} className="nw-btn px-4 py-2 bg-[#00f0ff] text-[#0a0a1f] font-tech uppercase tracking-wider text-xs" data-testid="nav-cta-chat-now">
            Chat with AI now
          </button>
        </div>
        <button className="md:hidden text-[#00f0ff]" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="nav-mobile-toggle">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t border-[#00f0ff]/20 px-6 py-4 flex flex-col gap-4" data-testid="nav-mobile-menu">
          {NAV.map(([id, label]) => (
            <button key={id} onClick={() => { setOpen(false); scrollToId(id); }} className="text-left font-tech text-sm uppercase tracking-widest text-slate-200">{label}</button>
          ))}
          <button onClick={() => navigate(accountBtn.to)} className="text-left font-tech text-sm uppercase tracking-widest text-slate-400">{accountBtn.label}</button>
          <button onClick={() => { setOpen(false); onChat(); }} className="nw-btn py-3 bg-[#00f0ff] text-[#0a0a1f] font-tech uppercase tracking-wider text-xs">Chat with AI now</button>
        </div>
      )}
    </nav>
  );
}

function Hero({ onChat, chatInputRef, onDispatch }) {
  return (
    <header className="relative overflow-hidden">
      <div className="nw-grid absolute inset-0 opacity-60" />
      <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-20 grid lg:grid-cols-2 gap-12 items-center">
        <div className="nw-rise">
          <p className="font-tech text-xs sm:text-sm uppercase tracking-[0.3em] text-[#00f0ff] mb-5" data-testid="hero-status-ticker">
            <span className="inline-block w-2 h-2 bg-[#39ff88] mr-2 animate-pulse" />Techs online · AI replies in seconds
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase tracking-tight leading-[1.05] text-white" data-testid="hero-main-heading">
            Tech help &amp; web builds,<br /><span className="text-[#ff00ff]">without the runaround.</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-300 max-w-xl leading-relaxed" data-testid="hero-sub-heading">
            Chat with our AI right now — no account, no login. If you need a real technician, a repair or a new website,
            we'll take it from there with clear, upfront prices.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-4">
            <button onClick={onChat} className="nw-btn flex items-center justify-center gap-2 px-7 py-4 bg-[#ff00ff] text-[#0a0a1f] hover:bg-white font-tech uppercase tracking-wider text-sm" data-testid="hero-cta-get-help-now">
              Get help now — no account needed <ArrowRight size={16} />
            </button>
            <button onClick={() => scrollToId('services')} className="nw-btn px-7 py-4 border border-[#00f0ff] text-[#00f0ff] hover:bg-[#00f0ff] hover:text-[#0a0a1f] font-tech uppercase tracking-wider text-sm" data-testid="hero-cta-view-services">
              See services &amp; prices
            </button>
          </div>
        </div>
        <div id="ai-console" className="nw-rise scroll-mt-24" style={{ animationDelay: '150ms' }}>
          <GuestChatConsole inputRef={chatInputRef} onDispatch={onDispatch} />
        </div>
      </div>
    </header>
  );
}

export default function LandingPage() {
  const [services, setServices] = useState([]);
  const [user, setUser] = useState(null);
  const [modal, setModal] = useState({ open: false, service: null, mode: 'support', transcript: [] });
  const chatInputRef = useRef(null);

  useEffect(() => {
    apiFetch(`${API}/services`, { silent: true })
      .then((r) => (r.ok ? r.json() : { services: [] }))
      .then((d) => setServices(d.services || []))
      .catch((err) => console.error('Error loading services:', err));
    if (getStoredToken()) {
      apiFetch(`${API}/auth/me`, { silent: true })
        .then((r) => (r.ok ? r.json() : null))
        .then(setUser)
        .catch((err) => console.debug('Not signed in', err));
    }
  }, []);

  const byCat = (cat) => services.filter((s) => s.category === cat);
  const find = (name) => services.find((s) => s.name === name);
  const openModal = (service, mode = 'support', transcript = []) => setModal({ open: true, service, mode, transcript });
  const focusChat = () => {
    scrollToId('ai-console');
    setTimeout(() => chatInputRef.current?.focus(), 450);
  };
  const managed = find('Managed Web Services');
  const webPackages = byCat('web').filter((s) => s.base_price > 0);

  return (
    <div className="nw-landing min-h-screen bg-[#0a0a1f]">
      <TopNav user={user} onChat={focusChat} />
      <Hero onChat={focusChat} chatInputRef={chatInputRef} onDispatch={(t) => openModal(null, 'support', t)} />
      <TestimonialBanner />

      <main className="max-w-7xl mx-auto px-6">
        <section className="pt-20 scroll-mt-20" id="services" data-testid="section-tech-support">
          <SectionHeading kicker="01 // Tech support & repair" title="Fix it, fast">
            Pick a service or just chat first. Every price is listed — no surprises.
          </SectionHeading>
          <div className="mb-8"><DiagnosticBanner /></div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {byCat('support').map((s) => <ServiceCard key={s.service_id} service={s} onStart={(svc) => openModal(svc)} />)}
          </div>
        </section>

        <section className="pt-24" data-testid="section-business-services">
          <SectionHeading kicker="02 // Business & specialized" title="For teams and offices" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {byCat('business').map((s) => (
              <ServiceCard key={s.service_id} service={s} onStart={(svc) => openModal(svc)} ctaLabel={s.requires_payment ? 'Get started' : 'Request a quote'} />
            ))}
          </div>
        </section>

        <section className="pt-24 scroll-mt-20" id="web-builds" data-testid="section-web-builds">
          <SectionHeading kicker="03 // Web builds" title="Websites that work as hard as you do">
            Value-based project pricing. Every build starts with a free consultation; a 50% non-refundable deposit kicks off the work.
          </SectionHeading>
          <div className="grid md:grid-cols-3 gap-6">
            {webPackages.map((s) => (
              <WebPackageCard key={s.service_id} service={s} featured={s.name === 'Vector Stack Build'} onConsult={(svc) => openModal(svc)} />
            ))}
          </div>
          {find('Custom Web Services') && (
            <button onClick={() => openModal(find('Custom Web Services'))} className="mt-6 font-tech text-sm text-[#00f0ff] hover:text-white uppercase tracking-wider flex items-center gap-2" data-testid="btn-custom-web-project">
              Not sure which fits? Tell us about your project <ArrowRight size={14} />
            </button>
          )}
        </section>

        {managed && (
          <section className="pt-24 scroll-mt-20" id="managed" data-testid="section-managed">
            <SectionHeading kicker="04 // Managed web services" title="We keep it running" />
            <ManagedPlanCard service={managed} onSubscribe={() => openModal(managed, 'subscribe')} onTalk={() => openModal(managed)} />
          </section>
        )}

        <section className="pt-24 scroll-mt-20" id="how-it-works">
          <SectionHeading kicker="05 // How it works" title="From problem to fixed" />
          <HowItWorks />
        </section>

        <section className="pt-24"><MissionStrip /></section>
      </main>

      <LandingFooter onChat={focusChat} />

      <GetStartedModal
        open={modal.open}
        onClose={() => setModal((m) => ({ ...m, open: false }))}
        services={services}
        initialService={modal.service}
        mode={modal.mode}
        transcript={modal.transcript}
        currentUser={user}
      />
    </div>
  );
}
